import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Auth } from "aws-amplify";
import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, useLocation } from "react-router-dom";

import {
  ApplicationError,
  useAnalytics,
  useLogout,
  useAppContext,
} from "@examspark/common-app";
import { Button } from "@examspark/common-ui";

import { ManageCalendars } from "../components/calendar";
import { CreateCourseCalendar } from "../components/calendar/course/CreateCourseCalendar";
import { EditCourseCalendarRoute } from "../components/calendar/course/EditCourseCalendar";
import { CreateWorkspaceCalendar } from "../components/calendar/workspace/CreateWorkspaceCalendar";
import { EditWorkspaceCalendarRoute } from "../components/calendar/workspace/EditWorkspaceCalendar";
import AppWrapper from "../components/common/AppWrapper";

import AccountRoute from "./private/AccountRoute";
import AssessmentRoute from "./private/AssessmentRoute";
import BrowseRoute from "./private/BrowseRoute";
import ClassroomRoute from "./private/ClassroomRoute";
import ClassroomsRoute from "./private/ClassroomsRoute";
import CourseRoute from "./private/CourseRoute";
import CourseVerificationRoute from "./private/CourseVerificationRoute";
import DashboardRoute from "./private/DashboardRoute";
import DocumentRoute from "./private/DocumentRoute";
import ExamDetailRoute from "./private/ExamDetailRoute";
import ExternalLinkRoute from "./private/ExternalLinkRoute";
import FavoritesRoute from "./private/FavoritesRoute";
import GlossaryRoute from "./private/GlossaryRoute";
import ItemRoute from "./private/ItemRoute";
import ObjectivesViewRoute from "./private/ObjectivesViewRoute";
import PagesRoute from "./private/pages/PagesRoute";
import PassageRoute from "./private/PassageRoute";
import RevisionRoute from "./private/RevisionRoute";
import RubricRoute from "./private/RubricRoute";
import StandardRoute from "./private/StandardRoute";
import UnitRoute from "./private/UnitRoute";
import UserRoute from "./private/UserRoute";
import VocabularyRoute from "./private/VocabularyRoute";
import WebDocumentRoute from "./private/WebDocumentRoute";
import WorkspaceRoute from "./private/WorkspaceRoute";
import { YearAtAGlanceRoute } from "./private/YearAtAGlanceRoute";

export const getUserAccessToken = async () => {
  const session = await Auth.currentSession();
  const accessToken = session.getAccessToken();
  return accessToken.getJwtToken();
};

const PrivateRoutes = () => {
  const [state] = useAppContext();
  const logout = useLogout();
  const analytics = useAnalytics();
  const location = useLocation();
  // const pathname = location.pathname;

  // intercept download requests
  const search = new URLSearchParams(location.search);
  const download = search.get("download");
  const origin = `${window.location.protocol}//${window.location.host}`;

  // intercept download requests
  useEffect(() => {
    if (download) {
      console.log("firing download hook");
      getUserAccessToken().then((token) => {
        const link = `${process.env.REACT_APP_REST_API_URL}/download?url=${download}&token=${token}&origin=${origin}`;
        window.location.href = link;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [download]);

  // analytics: identify user
  useEffect(() => {
    if (state.user) {
      analytics.identify(state.user, {
        workspaceId: state.workspaceUser?.id,
      });
    }
  }, [analytics, state.user, state.workspaceUser?.id]);

  // analytics: track every page navigation
  // useEffect(() => {
  //   analytics.trackEvent(AnalyticsEventName.PageView, {
  //     page: pathname,
  //   });
  // }, [analytics, pathname]);

  if (!state.user?.id || !state.workspaceUser?.id) {
    console.log("state?", state);
    console.warn("Missing state.user.id or state.workspaceUser.id");
    return (
      <ApplicationError
        buttons={
          <Button title="Logout" onClick={() => logout("/login")}>
            Clear Cache and Logout
          </Button>
        }
      />
    );
  }

  return (
    <>
      <AppWrapper>
        <ErrorBoundary
          fallback={<ApplicationError appName="Go TCMPC" />}
          onError={(error, info) => {
            console.error({ error, info });
          }}
        >
          <Routes>
            <Route path="/account/*" element={<AccountRoute />} />
            <Route path="/activities" element={<h1>Activities</h1>} />
            <Route
              path="/standardsview/:id"
              element={<ObjectivesViewRoute />}
            />
            <Route path="/assessment/:id" element={<AssessmentRoute />} />
            <Route path="/browse/*" element={<BrowseRoute />} />
            <Route path="/calendar">
              <Route
                path="workspace/new"
                element={<CreateWorkspaceCalendar />}
              />
              <Route
                path="workspace/:id"
                element={<EditWorkspaceCalendarRoute />}
              />

              <Route path="course/new" element={<CreateCourseCalendar />} />
              <Route
                path="course/:courseId/:courseCalendarId"
                element={<EditCourseCalendarRoute />}
              />

              <Route path={":tab"} element={<ManageCalendars />} />
              <Route index element={<ManageCalendars />} />
            </Route>
            <Route path="/classroom/:id/*" element={<ClassroomRoute />} />
            <Route path="/classrooms" element={<ClassroomsRoute />} />
            <Route
              path="/course/:id/verification"
              element={<CourseVerificationRoute />}
            />
            <Route path="/course/:id/*" element={<CourseRoute />} />
            <Route path="/course/:id/year" element={<YearAtAGlanceRoute />} />
            <Route path="/document/:id" element={<DocumentRoute />} />
            <Route path="/exam/:id/*" element={<ExamDetailRoute />} />
            <Route path="/favorites" element={<FavoritesRoute />} />
            <Route path="/glossary/*" element={<GlossaryRoute />} />
            <Route path="/item/:id" element={<ItemRoute />} />
            <Route path="/link/:id" element={<ExternalLinkRoute />} />
            <Route path="/pages/*" element={<PagesRoute />} />
            <Route path="/passage/:id" element={<PassageRoute />} />
            <Route path="/revisions" element={<RevisionRoute />} />
            <Route path="/rubric/:id" element={<RubricRoute />} />
            <Route path="/standard/:id" element={<StandardRoute />} />
            <Route path="/unit/:id" element={<UnitRoute />} />
            <Route path="/user/:workspaceId/:userId" element={<UserRoute />} />
            <Route path="/vocabulary/:id" element={<VocabularyRoute />} />
            <Route path="/webdoc/:id" element={<WebDocumentRoute />} />
            <Route path="/workspace">
              <Route index element={<WorkspaceRoute />} />
              <Route path=":tab" element={<WorkspaceRoute />} />
            </Route>
            <Route path="*" element={<DashboardRoute />} />
          </Routes>
        </ErrorBoundary>
      </AppWrapper>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

export default PrivateRoutes;
